"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _classnames = _interopRequireDefault(require("classnames"));
var _fieldFrame = _interopRequireDefault(require("@ux/field-frame"));
var _textEntry = _interopRequireDefault(require("@ux/text-entry"));
var _flyout = _interopRequireDefault(require("@ux/flyout"));
var _label = require("@ux/label");
var _text = _interopRequireDefault(require("@ux/text"));
const _excluded = ["className", "children", "label", "helpMessage", "errorMessage", "open", "visualSize"];
/* eslint-disable complexity */
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const UX_TEXT_INPUT_CLASSNAME = 'ux-text-input';
const UX_TEXT_INPUT_LABEL_FLOAT = false;

/**
 * Text input functional component
 * @param {Object} props - Information for the props can be found in the PropTypes below and in TextEntry propTypes
 * @param {String|React.Element|Object} [props.label] - Label for the component, also can take object as { content, float }
 * @param {String} [props.helpMessage] - Message for the component
 * @param {Node} [props.errorMessage] - Error message for the component
 * @param {('sm' | 'md')} [props.visualSize] - Sets component size, defaults to 'md'
 * @param {Object} ref - Forwarded ref to TextEntry
 * @returns {React.Component} TextInput component
 */
const TextInput = /*#__PURE__*/_react.default.forwardRef(function TextInput(_ref, ref) {
  let {
      className,
      children,
      label,
      helpMessage,
      errorMessage,
      open = true,
      visualSize = 'md'
    } = _ref,
    textEntryProps = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  const {
    id,
    invalid,
    disabled,
    required,
    labelExperimentalFloat
  } = textEntryProps || {};
  const helpMessageId = helpMessage && `${id}-help`;
  const errorMessageId = errorMessage && `${id}-error`;
  const anchorRef = (0, _react.useRef)(null);
  const show = open && children;

  // Determine if the label should float. Small components don't support floating labels.
  const float = visualSize !== 'sm' && labelExperimentalFloat || UX_TEXT_INPUT_LABEL_FLOAT;
  const classNames = (0, _classnames.default)(`${UX_TEXT_INPUT_CLASSNAME}-shell`, `${UX_TEXT_INPUT_CLASSNAME}--size-${visualSize}`, className);
  return /*#__PURE__*/_react.default.createElement("div", {
    "data-version": "2400",
    className: classNames
  }, !float && (0, _label.ensureLabelComponent)(label, {
    htmlFor: id,
    required
  }), /*#__PURE__*/_react.default.createElement(_fieldFrame.default, {
    disabled,
    invalid,
    ref: anchorRef
  }, /*#__PURE__*/_react.default.createElement(_textEntry.default, (0, _extends2.default)({}, textEntryProps, {
    "aria-invalid": invalid,
    "aria-errormessage": errorMessageId,
    "aria-describedby": helpMessageId,
    label: float && label,
    visualSize: visualSize,
    ref: ref
  })), show && /*#__PURE__*/_react.default.createElement(_flyout.default, {
    anchorRef: anchorRef,
    placement: 'bottom-start',
    stretch: true
  }, children)), helpMessage && /*#__PURE__*/_react.default.createElement(_text.default.p, {
    text: helpMessage,
    as: "caption",
    id: helpMessageId,
    emphasis: "passive"
  }), errorMessage && /*#__PURE__*/_react.default.createElement(_text.default.p, {
    text: errorMessage,
    as: "caption",
    id: errorMessageId,
    emphasis: "critical",
    role: "alert"
  }));
});
TextInput.propTypes = {
  /** Classes applied to the shell for positioning purposes */
  className: _propTypes.default.string,
  /** Provides default value */
  defaultValue: _propTypes.default.string,
  /** Sets the disabled state of the input */
  disabled: _propTypes.default.bool,
  /** Id applied to the input, used for the for attribute on the label */
  id: _propTypes.default.string.isRequired,
  /** Set component when input is invalid */
  invalid: _propTypes.default.bool,
  /** Optional label to float above input, placeholder will not show when using this */
  label: _propTypes.default.node,
  /** Uses a `<textarea/>` instead */
  multiline: _propTypes.default.bool,
  /** Blur handler for a input */
  onBlur: _propTypes.default.func,
  /** Change handler for a input */
  onChange: _propTypes.default.func,
  /** Focus handler for a input */
  onFocus: _propTypes.default.func,
  /** Placeholder for the input, do not supply with a floating label */
  placeholder: _propTypes.default.string,
  /** Allows a multiline input to be resized */
  resize: _propTypes.default.oneOfType([_propTypes.default.bool, _propTypes.default.oneOf(['horizontal', 'vertical'])]),
  /** Add asterik to supplied label, aria-required */
  required: _textEntry.default.propTypes.required,
  /** Set the size of the input */
  size: _propTypes.default.number,
  /** Text to suggest as completion of the input */
  suggestion: _propTypes.default.string,
  /** Input type */
  type: _textEntry.default.propTypes.type,
  /** Value for the input */
  value: _propTypes.default.node,
  /** Optional message which appears below the input */
  helpMessage: _propTypes.default.string,
  /** Optional error message which appears below the input */
  errorMessage: _propTypes.default.node,
  /** Optionally show children within a flyout */
  children: _propTypes.default.node,
  /** Shows children in flyout when provided */
  open: _propTypes.default.bool,
  /** Component size, default is 'md' */
  visualSize: _propTypes.default.oneOf(['sm', 'md'])
};
TextInput.displayName = 'TextInput';
var _default = exports.default = TextInput;